<template>
  <v-main>
    <v-container fluid>
      <!-- Summary Cards -->
      <v-row align="stretch">
        <v-col cols="12" sm="6" md="3">
          <v-card
            elevation="6"
            rounded="xxl"
            color="deep-purple"
            dark
            class="fill-height stat-card"
          >
            <v-card-text>
              <div class="text-caption stat-label">Direct Expenses</div>
              <h2 class="font-weight-bold stat-value">
                {{ formatAmount(summary.total_direct) }}
              </h2>
              <div class="text-caption stat-sub">
                {{ directShare }}% of total
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card
            elevation="6"
            rounded="xxl"
            color="blue darken-2"
            dark
            class="fill-height stat-card"
          >
            <v-card-text>
              <div class="text-caption stat-label">Indirect Expenses</div>
              <h2 class="font-weight-bold stat-value">
                {{ formatAmount(summary.total_indirect) }}
              </h2>
              <div class="text-caption stat-sub">
                {{ indirectShare }}% of total
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card
            elevation="6"
            rounded="xxl"
            color="teal darken-1"
            dark
            class="fill-height stat-card"
          >
            <v-card-text>
              <div class="text-caption stat-label">Total Expenses</div>
              <h2 class="font-weight-bold stat-value">
                {{ formatAmount(summary.total_expenses) }}
              </h2>
              <div class="text-caption stat-sub">
                across {{ categoryBreakdown.length }} categories
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card
            elevation="6"
            rounded="xxl"
            color="blue-grey darken-1"
            dark
            class="fill-height stat-card"
          >
            <v-card-text>
              <div class="text-caption stat-label">Entries</div>
              <h2 class="font-weight-bold stat-value">
                {{ summary.total_entries || 0 }}
              </h2>
              <div class="text-caption stat-sub">
                Avg {{ formatAmount(avgEntry) }} each
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Header / Filters -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Expenses</h3>
              <v-btn
                class="mx-2 mb-2"
                fab
                x-small
                color="primary"
                to="/expenses/create"
              >
                <v-icon dark>mdi-plus</v-icon>
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                class="mb-2"
                rounded
                small
                color="success"
                :loading="isExporting"
                @click="exportExcel"
              >
                <v-icon left small>mdi-microsoft-excel</v-icon>
                Export
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="4">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search title, EXP # or reference..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>
                <v-col sm="2">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.expense_type"
                    :items="typeOptions"
                    label="Type"
                    clearable
                    @click:clear="
                      (filter.expense_type = null), (page = 1), reload()
                    "
                    @input="(page = 1), reload()"
                    prepend-inner-icon="mdi-shape-outline"
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.expense_category_id"
                    :items="filteredCategories"
                    item-text="name"
                    item-value="id"
                    label="Category"
                    clearable
                    @click:clear="
                      (filter.expense_category_id = null), (page = 1), reload()
                    "
                    @input="(page = 1), reload()"
                    prepend-inner-icon="mdi-tag-outline"
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.payment_mode"
                    :items="paymentModes"
                    label="Payment Mode"
                    clearable
                    @click:clear="
                      (filter.payment_mode = null), (page = 1), reload()
                    "
                    @input="(page = 1), reload()"
                    prepend-inner-icon="mdi-cash"
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.start_date"
                    type="date"
                    label="From Date"
                    clearable
                    @change="(page = 1), reload()"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.end_date"
                    type="date"
                    label="To Date"
                    clearable
                    @change="(page = 1), reload()"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Table -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="500"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Expense No</th>
                      <th class="text-left">Date</th>
                      <th class="text-left">Type</th>
                      <th class="text-left">Category</th>
                      <th class="text-left">Title</th>
                      <th class="text-right">Amount</th>
                      <th class="text-left">Mode</th>
                      <th class="text-left">Reference</th>
                      <th class="text-right action_th">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="expenses.length">
                    <tr v-for="(expense, i) in expenses" :key="`expense_${i}`">
                      <td style="min-width: 85px">
                        {{ (page - 1) * rowsPerPage + i + 1 }}
                      </td>
                      <td>{{ expense.expense_number }}</td>
                      <td>{{ expense.expense_date | niceDate }}</td>
                      <td>
                        <v-chip
                          small
                          :color="
                            expense.expense_type === 0 ? 'deep-purple' : 'blue'
                          "
                          text-color="white"
                        >
                          {{ expense.expense_type === 0 ? "Direct" : "Indirect" }}
                        </v-chip>
                      </td>
                      <td>{{ expense.category ? expense.category.name : "" }}</td>
                      <td style="max-width: 260px">{{ expense.title }}</td>
                      <td class="text-right">{{ expense.amount }}</td>
                      <td>{{ paymentModeLabel(expense.payment_mode) }}</td>
                      <td>{{ expense.reference_number || "-" }}</td>
                      <td class="text-right" style="min-width: 140px">
                        <v-tooltip bottom v-if="expense.attachment_path">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="mr-1"
                              color="blue-grey"
                              :href="mediaUrl + expense.attachment_path"
                              target="_blank"
                            >
                              <v-icon dark>mdi-paperclip</v-icon>
                            </v-btn>
                          </template>
                          <span>View Bill / Receipt</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="mr-1"
                              color="primary"
                              :to="`/expenses/${encryptIt(expense.id)}`"
                            >
                              <v-icon dark>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="error"
                              @click="askDelete(expense)"
                            >
                              <v-icon dark>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="10" class="text-center">
                        <v-alert type="info" text>No expenses found</v-alert>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination
                v-if="paginationLength > 1"
                v-model="page"
                :length="paginationLength"
              ></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Category Breakdown -->
      <v-row v-if="categoryBreakdown.length">
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h4 class="font-weight-bold">Category Breakdown</h4>
            </v-card-title>
            <v-card-text>
              <v-simple-table class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Category</th>
                      <th class="text-left">Type</th>
                      <th class="text-right">Entries</th>
                      <th class="text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in categoryBreakdown" :key="`cb_${i}`">
                      <td>{{ row.category_name }}</td>
                      <td>
                        <v-chip
                          small
                          :color="row.expense_type === 0 ? 'deep-purple' : 'blue'"
                          text-color="white"
                        >
                          {{ row.expense_type_label }}
                        </v-chip>
                      </td>
                      <td class="text-right">{{ row.entry_count }}</td>
                      <td class="text-right">
                        {{ formatAmount(row.total_amount) }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete Confirmation -->
    <v-dialog v-model="deleteDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: orange; font-size: 90px">
                mdi-alert
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: orange">Confirm Delete</h1>
                <br />
                <h3>
                  Delete {{ deleteTarget ? deleteTarget.expense_number : "" }}?
                </h3>
                <br />
                <v-btn
                  color="grey"
                  text
                  class="mr-2"
                  @click="deleteDialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn color="error" @click="confirmDelete">Delete</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Success -->
    <v-dialog v-model="successDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: green; font-size: 90px">
                mdi-check-circle
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: green">Success!</h1>
                <br />
                <h3>{{ successMessage }}</h3>
                <br />
                <v-btn color="success" @click="successDialog = false">OK</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Error -->
    <v-dialog v-model="errorDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: red; font-size: 90px">
                mdi-alert-circle
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: red">Error!</h1>
                <br />
                <h3>{{ errorMessage }}</h3>
                <br />
                <v-btn color="error" @click="errorDialog = false">OK</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "ExpensesPage",
  data() {
    return {
      isLoading: false,
      isExporting: false,
      expenses: [],
      categories: [],
      summary: {},
      categoryBreakdown: [],
      page: 1,
      paginationLength: 0,
      count: 0,
      deleteDialog: false,
      deleteTarget: null,
      successDialog: false,
      errorDialog: false,
      successMessage: "",
      errorMessage: "",
      filter: {
        search_keyword: null,
        expense_type: null,
        expense_category_id: null,
        payment_mode: null,
        start_date: null,
        end_date: null,
      },
      typeOptions: [
        { text: "Direct", value: 0 },
        { text: "Indirect", value: 1 },
      ],
      paymentModes: [
        { text: "Cash", value: 0 },
        { text: "Bank Transfer", value: 1 },
        { text: "Cheque", value: 2 },
        { text: "UPI", value: 3 },
        { text: "Other", value: 4 },
      ],
    };
  },
  computed: {
    directShare() {
      const total = Number(this.summary.total_expenses || 0);
      if (!total) return 0;
      return Math.round((Number(this.summary.total_direct || 0) / total) * 100);
    },

    indirectShare() {
      const total = Number(this.summary.total_expenses || 0);
      if (!total) return 0;
      return Math.round(
        (Number(this.summary.total_indirect || 0) / total) * 100,
      );
    },

    avgEntry() {
      const entries = Number(this.summary.total_entries || 0);
      if (!entries) return 0;
      return Number(this.summary.total_expenses || 0) / entries;
    },

    // Keep the category dropdown consistent with the type filter above it.
    filteredCategories() {
      if (this.filter.expense_type === 0 || this.filter.expense_type) {
        return this.categories.filter(
          (c) => c.type === this.filter.expense_type,
        );
      }
      return this.categories;
    },
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getMasters();
    this.reload();
  },
  methods: {
    reload() {
      this.getData();
      this.getSummary();
    },

    async getMasters() {
      try {
        const response = await axios.get("/masters", {
          params: { context: "expense" },
        });
        this.categories = response.data.expense_categories || [];
      } catch (error) {
        console.log(error);
      }
    },

    buildParams() {
      const params = {};
      Object.keys(this.filter).forEach((key) => {
        const value = this.filter[key];
        if (value === 0 || (value !== null && value !== undefined && value !== "")) {
          params[key] = value;
        }
      });
      return params;
    },

    async getData() {
      this.isLoading = true;
      try {
        const params = this.buildParams();
        params.page = this.page;
        params.rowsPerPage = this.rowsPerPage;

        const response = await axios.get("/expenses", { params });
        this.expenses = response.data.data || [];
        this.count = response.data.count || 0;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.log(error);
        this.expenses = [];
      }
      this.isLoading = false;
    },

    async getSummary() {
      try {
        const response = await axios.get("/expenses/summary", {
          params: this.buildParams(),
        });
        const data = response.data.data || {};
        this.summary = data.summary || {};
        this.categoryBreakdown = data.category_breakdown || [];
      } catch (error) {
        console.log(error);
      }
    },

    askDelete(expense) {
      this.deleteTarget = expense;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.deleteDialog = false;
      try {
        await axios.post(
          `/expenses/delete/${this.encryptIt(this.deleteTarget.id)}`,
        );
        this.successMessage = "Expense deleted successfully";
        this.successDialog = true;
        this.reload();
      } catch (error) {
        console.log(error);
        this.errorMessage =
          (error.response && error.response.data && error.response.data.message) ||
          "Failed to delete expense";
        this.errorDialog = true;
      }
      this.deleteTarget = null;
    },

    async exportExcel() {
      this.isExporting = true;
      try {
        const response = await axios.get("/expenses/excel", {
          params: this.buildParams(),
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `expenses_${new Date().toISOString().slice(0, 10)}.xlsx`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        this.errorMessage = "Failed to export expenses";
        this.errorDialog = true;
      }
      this.isExporting = false;
    },

    paymentModeLabel(mode) {
      const found = this.paymentModes.find((m) => m.value === mode);
      return found ? found.text : "-";
    },

    formatDate(date) {
      return date ? String(date).slice(0, 10) : "";
    },

    formatAmount(value) {
      const amount = Number(value || 0);
      return `₹ ${amount.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
};
</script>

<style scoped>
/* Lock the three rows to fixed heights so the labels, figures and sub-lines
   line up across all four cards even when one amount wraps. */
.stat-card .stat-label {
  min-height: 18px;
  opacity: 0.85;
}
.stat-card .stat-value {
  min-height: 34px;
  line-height: 34px;
  white-space: nowrap;
}
.stat-card .stat-sub {
  min-height: 18px;
  opacity: 0.85;
}
</style>
